import { TrackingType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../utils/prepHeaders';

type T = typeof TrackingType;
type Code = { code: string };

const blob = async (response: Response) => await response.blob();

// Live tracking data must never be served from a cached result.
const live = { keepUnusedDataFor: 0 } as const;

export const trackingApi = createApi({
  reducerPath: 'trackingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'TrackingItem',
    'TrackingItems',
    'TrackingTemplates',
    'TrackingProfiles',
    'TrackingJobs',
    'TrackingDefaults',
  ],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getFormats: builder.query<TrackingType.FormatsResponse, void>({
      query: () => ({ url: '/tracking/formats/' }),
    }),
    getSources: builder.query<
      TrackingType.Paged<TrackingType.SourceRow>,
      Record<string, string | number | undefined>
    >({
      query: (params) => ({ url: '/tracking/sources/', params }),
      ...live,
    }),
    getFrameUnits: builder.query<
      TrackingType.FrameUnitsResponse,
      { frame_id: number }
    >({
      query: ({ frame_id }) => ({
        url: `/tracking/sources/frames/${frame_id}/units/`,
      }),
      providesTags: ['TrackingItems'],
      ...live,
    }),
    postRegister: builder.mutation<
      { items: TrackingType.ItemSummary[]; created?: boolean },
      {
        source_type: string;
        source_id: number;
        unit_indexes?: number[];
        request_id?: string;
      }
    >({
      query: (body) => ({ url: '/tracking/register/', method: 'POST', body }),
      invalidatesTags: ['TrackingItems'],
    }),
    postResolveReconciliation: builder.mutation<
      unknown,
      {
        id: number;
        decision: 'RESTORE' | 'ISSUE_NEW';
        unit_ids?: number[];
        request_id: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/tracking/reconciliations/${id}/resolve/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TrackingItems'],
    }),
    getItems: builder.query<
      TrackingType.Paged<TrackingType.ItemSummary>,
      Record<string, string | number | undefined>
    >({
      query: (params) => ({ url: '/tracking/items/', params }),
      providesTags: ['TrackingItems'],
      ...live,
    }),
    postItem: builder.mutation<
      TrackingType.ItemDetail,
      Record<string, unknown>
    >({
      query: (body) => ({ url: '/tracking/items/', method: 'POST', body }),
      invalidatesTags: ['TrackingItems'],
    }),
    getResolve: builder.query<
      TrackingType.ResolveResponse,
      { code: string; prefix?: string; suffix?: string }
    >({
      query: (params) => ({ url: '/tracking/resolve/', params }),
      ...live,
    }),
    getItem: builder.query<TrackingType.ItemDetail, Code>({
      query: ({ code }) => ({ url: `/tracking/items/${code}/` }),
      providesTags: (_r, _e, { code }) => [{ type: 'TrackingItem', id: code }],
      ...live,
    }),
    getReport: builder.query<
      TrackingType.ItemReport,
      Code & { offset?: number; limit?: number }
    >({
      query: ({ code, ...params }) => ({
        url: `/tracking/items/${code}/report/`,
        params,
      }),
      providesTags: (_r, _e, { code }) => [{ type: 'TrackingItem', id: code }],
      ...live,
    }),
    getEvents: builder.query<
      TrackingType.HistoryPage,
      Code & { offset: number; limit: number }
    >({
      query: ({ code, ...params }) => ({
        url: `/tracking/items/${code}/events/`,
        params,
      }),
      providesTags: (_r, _e, { code }) => [{ type: 'TrackingItem', id: code }],
      ...live,
    }),
    getReportPdf: builder.mutation<Blob, Code & { paper: 'letter' | 'a4' }>({
      query: ({ code, paper }) => ({
        url: `/tracking/items/${code}/report.pdf`,
        params: { paper },
        responseHandler: blob,
        validateStatus: (response: Response) => response.status === 200,
      }),
    }),
    patchItem: builder.mutation<
      TrackingType.ItemDetail,
      Code & Record<string, unknown>
    >({
      query: ({ code, ...body }) => ({
        url: `/tracking/items/${code}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { code }) => [
        { type: 'TrackingItem', id: code },
        'TrackingItems',
      ],
    }),
    postNote: builder.mutation<
      unknown,
      Code & { body: string; request_id: string }
    >({
      query: ({ code, ...body }) => ({
        url: `/tracking/items/${code}/notes/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { code }) => [
        { type: 'TrackingItem', id: code },
      ],
    }),
    postItemAction: builder.mutation<
      TrackingType.ItemDetail,
      Code & {
        action: 'link' | 'merge' | 'void';
        body: Record<string, unknown>;
      }
    >({
      query: ({ code, action, body }) => ({
        url: `/tracking/items/${code}/${action}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { code }) => [
        { type: 'TrackingItem', id: code },
        'TrackingItems',
      ],
    }),
    postVerifyLabel: builder.mutation<
      { matches: boolean },
      Code & {
        scanned_value: string;
        method: string;
        job_id?: string;
        request_id: string;
      }
    >({
      query: ({ code, ...body }) => ({
        url: `/tracking/items/${code}/verify-label/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { code }) => [
        { type: 'TrackingItem', id: code },
      ],
    }),
    postAttachmentPresign: builder.mutation<
      {
        key: string;
        method: 'PUT' | 'API';
        url: string | null;
        headers: Record<string, string>;
      },
      Code & { filename: string; content_type: string; size: number }
    >({
      query: ({ code, ...body }) => ({
        url: `/tracking/items/${code}/attachments/presign/`,
        method: 'POST',
        body,
      }),
    }),
    postAttachmentFinalize: builder.mutation<
      { id: number },
      Code & { body: Record<string, unknown> | FormData }
    >({
      query: ({ code, body }) => ({
        url: `/tracking/items/${code}/attachments/`,
        method: 'POST',
        body,
        headers:
          body instanceof FormData ? { 'X-FormData': 'true' } : undefined,
      }),
      invalidatesTags: (_r, _e, { code }) => [
        { type: 'TrackingItem', id: code },
      ],
    }),
    uploadAttachment: builder.mutation<
      { id: number },
      Code & { file: File; caption?: string }
    >({
      async queryFn({ code, file, caption = '' }, _api, _extra, baseQuery) {
        const presigned = await baseQuery({
          url: `/tracking/items/${code}/attachments/presign/`,
          method: 'POST',
          body: {
            filename: file.name,
            content_type: file.type,
            size: file.size,
          },
        });
        if (presigned.error) return { error: presigned.error };
        const target = presigned.data as {
          key: string;
          method: 'PUT' | 'API';
          url: string | null;
          headers: Record<string, string>;
        };
        if (target.method === 'API') {
          const form = new FormData();
          form.append('file', file);
          form.append('caption', caption);
          const res = await baseQuery({
            url: `/tracking/items/${code}/attachments/`,
            method: 'POST',
            body: form,
            headers: { 'X-FormData': 'true' },
          });
          return res.error
            ? { error: res.error }
            : { data: res.data as { id: number } };
        }
        const put = await fetch(target.url!, {
          method: 'PUT',
          body: file,
          headers: target.headers,
        });
        if (!put.ok)
          return {
            error: {
              status: put.status,
              data: { detail: 'The file could not be uploaded to storage.' },
            },
          };
        const res = await baseQuery({
          url: `/tracking/items/${code}/attachments/`,
          method: 'POST',
          body: {
            key: target.key,
            filename: file.name,
            content_type: file.type,
            size: file.size,
            caption,
          },
        });
        return res.error
          ? { error: res.error }
          : { data: res.data as { id: number } };
      },
      invalidatesTags: (_r, _e, { code }) => [
        { type: 'TrackingItem', id: code },
      ],
    }),
    getAttachmentDownload: builder.mutation<
      { url: string | null; expires_in: number } | Blob,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/tracking/attachments/${id}/download/`,
        responseHandler: async (response: Response) =>
          response.headers.get('Content-Type')?.includes('application/json')
            ? response.json()
            : response.blob(),
      }),
    }),
    getTemplates: builder.query<{ items: TrackingType.TemplateRecord[] }, void>(
      {
        query: () => ({ url: '/tracking/templates/' }),
        providesTags: ['TrackingTemplates'],
        ...live,
      },
    ),
    postTemplate: builder.mutation<
      TrackingType.TemplateRecord,
      { name: string; is_shared?: boolean; spec: unknown }
    >({
      query: (body) => ({ url: '/tracking/templates/', method: 'POST', body }),
      invalidatesTags: ['TrackingTemplates'],
    }),
    patchTemplate: builder.mutation<
      TrackingType.TemplateRecord,
      { id: number } & Record<string, unknown>
    >({
      query: ({ id, ...body }) => ({
        url: `/tracking/templates/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['TrackingTemplates'],
    }),
    postTemplateAction: builder.mutation<
      unknown,
      { id: number; action: 'duplicate' | 'archive' }
    >({
      query: ({ id, action }) => ({
        url: `/tracking/templates/${id}/${action}/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['TrackingTemplates', 'TrackingDefaults'],
    }),
    getProfiles: builder.query<{ items: TrackingType.ProfileRecord[] }, void>({
      query: () => ({ url: '/tracking/printer-profiles/' }),
      providesTags: ['TrackingProfiles'],
      ...live,
    }),
    postProfile: builder.mutation<
      TrackingType.ProfileRecord,
      Record<string, unknown>
    >({
      query: (body) => ({
        url: '/tracking/printer-profiles/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TrackingProfiles'],
    }),
    patchProfile: builder.mutation<
      TrackingType.ProfileRecord,
      { id: number } & Record<string, unknown>
    >({
      query: ({ id, ...body }) => ({
        url: `/tracking/printer-profiles/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['TrackingProfiles', 'TrackingDefaults'],
    }),
    getDefaults: builder.query<TrackingType.UserDefaults, void>({
      query: () => ({ url: '/tracking/defaults/' }),
      providesTags: ['TrackingDefaults'],
      ...live,
    }),
    putDefaults: builder.mutation<
      TrackingType.UserDefaults,
      TrackingType.UserDefaults
    >({
      query: (body) => ({ url: '/tracking/defaults/', method: 'PUT', body }),
      invalidatesTags: ['TrackingDefaults'],
    }),
    postPreview: builder.mutation<
      TrackingType.PreviewResponse,
      Record<string, unknown>
    >({
      query: (body) => ({
        url: '/tracking/labels/preview/',
        method: 'POST',
        body,
      }),
    }),
    postCalibration: builder.mutation<Blob, Record<string, unknown>>({
      query: (body) => ({
        url: '/tracking/labels/calibration/',
        method: 'POST',
        body,
        responseHandler: blob,
        validateStatus: (response: Response) => response.status === 200,
      }),
    }),
    getJobs: builder.query<
      TrackingType.Paged<TrackingType.PrintJob>,
      { offset?: number; limit?: number; status?: string }
    >({
      query: (params) => ({ url: '/tracking/print-jobs/', params }),
      providesTags: ['TrackingJobs'],
      ...live,
    }),
    getJob: builder.query<TrackingType.PrintJob, { id: string }>({
      query: ({ id }) => ({ url: `/tracking/print-jobs/${id}/` }),
      providesTags: (_r, _e, { id }) => [{ type: 'TrackingJobs', id }],
      ...live,
    }),
    postJob: builder.mutation<TrackingType.PrintJob, Record<string, unknown>>({
      query: (body) => ({ url: '/tracking/print-jobs/', method: 'POST', body }),
      invalidatesTags: ['TrackingJobs', 'TrackingItems'],
    }),
    postJobAction: builder.mutation<
      TrackingType.PrintJob,
      {
        id: string;
        action: 'retry' | 'cancel' | 'print-request' | 'confirm';
        request_id?: string;
      }
    >({
      query: ({ id, action, request_id }) => ({
        url: `/tracking/print-jobs/${id}/${action}/`,
        method: 'POST',
        body: { request_id },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        'TrackingJobs',
        { type: 'TrackingJobs', id },
      ],
    }),
    getJobOutput: builder.mutation<
      Blob,
      { id: string; kind?: 'pdf' | 'svg' | 'png'; index?: number }
    >({
      query: ({ id, kind = 'pdf', index }) => ({
        url: `/tracking/print-jobs/${id}/output/`,
        params: { kind, index },
        responseHandler: blob,
        validateStatus: (response: Response) => response.status === 200,
      }),
    }),
  }),
});

export namespace TrackingApi {
  export const Api = trackingApi;
  export type Types = T;
}
